<template>
  <section class="overflow-hidden rounded-lg border border-gray-200 bg-white">
    <div class="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 px-4 py-4">
      <div>
        <h3 class="text-sm font-semibold text-gray-800">Entity relationships</h3>
        <p class="mt-1 text-xs text-gray-500">Parent → child, top to bottom. Crowded levels wrap onto extra rows; drag horizontally to rearrange.</p>
      </div>
    </div>
    <div ref="container" class="relative overflow-hidden bg-gray-25" :style="{ height: `${height}px` }">
      <LoadingIndicator v-if="loading" class="absolute inset-0" message="Loading entity relationships…" />
      <div v-else-if="error" role="alert" class="absolute inset-0 flex items-center justify-center gap-2 px-4 text-xs text-gray-600">
        <span>{{ error }}</span>
        <button type="button" class="btn-secondary-sm" @click="loadGraph">Retry</button>
      </div>
      <div v-else-if="!nodes.length" class="absolute inset-0 flex items-center justify-center text-xs text-gray-500">No entity types to display.</div>
      <svg ref="svg" v-show="!loading && !error && nodes.length" class="h-full w-full" :viewBox="`0 0 ${width} ${height}`" role="group" aria-label="Force-directed graph of entity type relationships">
        <defs>
          <marker :id="markerId" viewBox="0 -5 10 10" refX="9" refY="0" markerWidth="7" markerHeight="7" orient="auto-start-reverse" markerUnits="userSpaceOnUse">
            <path d="M0,-4L9,0L0,4Z" class="fill-gray-400" />
          </marker>
        </defs>
        <g ref="graphLayer">
          <path v-for="segment in renderedConnections.segments" :key="segment.key" :d="segment.path" fill="none" :stroke-width="isRelatedConnection(segment) ? 2 : 1.2" :class="isRelatedConnection(segment) ? 'stroke-primary-500' : 'stroke-gray-300'" :opacity="activeId !== null && !isRelatedConnection(segment) ? 0.2 : 1">
            <title>{{ segment.links.map(link => linkLabel(link)).join('; ') }}</title>
          </path>
          <path v-for="arrow in renderedConnections.arrows" :key="arrow.key" :d="arrow.path" fill="none" :marker-end="`url(#${markerId})`" :opacity="activeId !== null && !isRelatedConnection(arrow) ? 0.2 : 1" />
          <g v-for="node in nodes" :key="node.id" class="entity-relationship-node cursor-ew-resize outline-none" :transform="`translate(${node.x || 0},${node.y || 0})`" tabindex="0" role="button" :aria-label="`${node.name}: ${node.count.toLocaleString()} entities. Highlight relationships.`" :aria-pressed="selectedId === node.id" @mouseenter="hoveredId = node.id" @mouseleave="hoveredId = null" @focus="hoveredId = node.id" @blur="hoveredId = null" @click="toggleNode(node.id)" @keydown.enter.prevent="toggleNode(node.id)" @keydown.space.prevent="toggleNode(node.id)" @keydown.esc="$emit('select', null)">
            <rect :x="-node.width / 2" :y="-node.height / 2" :width="node.width" :height="node.height" rx="8" stroke-width="1.5" :class="activeId === node.id ? 'fill-primary-50 stroke-primary-500' : 'fill-white stroke-neutral-400'" :opacity="activeId !== null && !isRelatedNode(node.id) ? 0.4 : 1" />
            <text text-anchor="middle" class="fill-gray-800 text-[11px] font-medium pointer-events-none" :opacity="activeId !== null && !isRelatedNode(node.id) ? 0.4 : 1">
              <tspan v-for="(line, index) in node.lines" :key="index" x="0" :y="-node.height / 2 + 16 + index * 12">{{ line }}</tspan>
              <tspan v-for="(line, index) in node.identifierLines" :key="`identifier-${index}`" x="0" :y="-node.height / 2 + 18 + node.lines.length * 12 + index * 10" class="fill-neutral-600 text-[10px] font-normal">{{ line }}</tspan>
              <tspan x="0" :y="node.height / 2 - 7" class="fill-gray-500 text-[10px] font-normal">{{ node.count.toLocaleString() }} {{ node.count === 1 ? 'entity' : 'entities' }}</tspan>
            </text>
          </g>
        </g>
      </svg>
    </div>
    <div class="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-4 py-2 text-xs text-gray-500" aria-live="polite">
      <span v-if="!loading && !error">{{ nodes.length }} types · {{ links.length }} relationship types · {{ relationshipCount.toLocaleString() }} direct links</span>
      <span v-else>Entity type relationships</span>
      <span>{{ activeNode ? `${activeNode.name} — connected types highlighted` : 'Arrows point to children · Rows are fixed · Drag nodes horizontally' }}</span>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import { forceSimulation, forceLink, forceManyBody, forceX, select, drag } from 'd3';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import abortable from '@/mixins/abortable';

let graphCounter = 0;

export default {
  name: 'EntityTypeRelationshipGraph',
  components: { LoadingIndicator },
  mixins: [abortable],
  props: {
    entityTypes: { type: Array, default: () => [] },
    selectedTypeId: { type: Number, default: null },
  },
  emits: ['select'],
  data() {
    return {
      nodes: [],
      links: [],
      loading: true,
      error: '',
      width: 1000,
      height: 400,
      hoveredId: null,
      selectedId: null,
      markerId: `entity-type-arrow-${++graphCounter}`,
    };
  },
  computed: {
    tenantSlug() {
      return this.$store.getters['tenant/current_tenant_slug'];
    },
    typeSignature() {
      return this.entityTypes.map(type => `${type.id}:${type.type_name}`).join('|');
    },
    activeId() {
      return this.hoveredId ?? this.selectedId;
    },
    activeNode() {
      return this.nodes.find(node => node.id === this.activeId);
    },
    renderedConnections() {
      const lanes = new Map();
      const arrows = new Map();
      const round = value => Math.round(value * 1000) / 1000;
      for (const link of this.links) {
        const points = this.linkPoints(link).map(([x, y]) => [round(x), round(y)]);
        if (points.length < 2) continue;
        let lastSegment;
        for (let index = 1; index < points.length; index += 1) {
          const [x1, y1] = points[index - 1];
          const [x2, y2] = points[index];
          if (x1 === x2 && y1 === y2) continue;
          const vertical = x1 === x2;
          const fixed = vertical ? x1 : y1;
          const start = Math.min(vertical ? y1 : x1, vertical ? y2 : x2);
          const end = Math.max(vertical ? y1 : x1, vertical ? y2 : x2);
          const key = `${vertical ? 'v' : 'h'}:${fixed}`;
          if (!lanes.has(key)) lanes.set(key, { key, vertical, fixed, intervals: [] });
          lanes.get(key).intervals.push({ start, end, link });
          lastSegment = { x1, y1, x2, y2 };
        }
        if (lastSegment) {
          const { x1, y1, x2, y2 } = lastSegment;
          const dx = Math.sign(x2 - x1);
          const dy = Math.sign(y2 - y1);
          const key = `arrow:${x2}:${y2}:${dx}:${dy}`;
          if (!arrows.has(key)) arrows.set(key, { key, path: `M${x2 - dx},${y2 - dy} L${x2},${y2}`, links: [] });
          arrows.get(key).links.push(link);
        }
      }
      const segments = [];
      for (const lane of lanes.values()) {
        const points = [...new Set(lane.intervals.flatMap(interval => [interval.start, interval.end]))].sort((a, b) => a - b);
        for (let index = 1; index < points.length; index += 1) {
          const start = points[index - 1];
          const end = points[index];
          const members = lane.intervals.filter(interval => interval.start <= start && interval.end >= end);
          if (!members.length) continue;
          const links = [...new Map(members.map(member => [member.link.key, member.link])).values()];
          segments.push({
            key: `${lane.key}:${start}:${end}`,
            path: lane.vertical ? `M${lane.fixed},${start} V${end}` : `M${start},${lane.fixed} H${end}`,
            links,
          });
        }
      }
      return { segments, arrows: [...arrows.values()] };
    },
    relationshipCount() {
      return this.links.reduce((total, link) => total + link.count, 0);
    },
  },
  watch: {
    selectedTypeId(val) { this.selectedId = val; },
    typeSignature() {
      if (this._ready) this.loadGraph();
    },
    tenantSlug() {
      if (this._ready) this.loadGraph();
    },
  },
  mounted() {
    this._ready = true;
    this._observer = new ResizeObserver(() => this.resizeGraph());
    this._observer.observe(this.$refs.container);
    this.loadGraph();
  },
  beforeUnmount() {
    this._ready = false;
    this._simulation?.stop();
    this._observer?.disconnect();
  },
  methods: {
    async loadGraph() {
      const signal = this._renewAbortSignal();
      this._simulation?.stop();
      this.loading = true;
      this.error = '';
      this.nodes = [];
      this.links = [];
      this.selectedId = this.selectedTypeId;
      this.hoveredId = null;
      try {
        const { data } = await axios.get('/api/entity-types/relationships/', { signal, params: { tenant_slug: this.tenantSlug } });
        if (signal.aborted) return;
        this.width = this.$refs.container.clientWidth || 1000;
        this.nodes = data.nodes.map(node => {
          const lines = this.wrapLabel(node.name);
          const identifierLines = node.identifier_types?.length ? this.wrapLabel(node.identifier_types.join(' · ').replaceAll('_', ' ')) : [];
          const naturalHeight = lines.length * 12 + 24 + identifierLines.length * 10 + (identifierLines.length ? 4 : 0);
          return {
            ...node,
            lines,
            identifierLines,
            width: Math.max(90, Math.max(...lines.map(line => line.length)) * 6 + 20, ...identifierLines.map(line => line.length * 5.5 + 20)),
            naturalHeight,
            height: naturalHeight,
            x: this.width / 2,
            y: 0,
          };
        });
        this.links = data.links.map(link => ({ ...link, key: `${link.source}:${link.target}` }));
        this.loading = false;
        await this.$nextTick();
        if (signal.aborted || !this._ready) return;
        this.layoutGraph();
      } catch (error) {
        if (this._isAborted(error)) return;
        this.error = 'Could not load entity relationships.';
      } finally {
        if (!signal.aborted) this.loading = false;
      }
    },
    wrapLabel(label) {
      const lines = [];
      for (const word of label.split(/\s+/)) {
        if (lines.length && `${lines[lines.length - 1]} ${word}`.length <= 22) lines[lines.length - 1] += ` ${word}`;
        else lines.push(word);
      }
      return lines.length ? lines : ['Unnamed type'];
    },
    assignRows() {
      const children = new Map(this.nodes.map(node => [node.id, []]));
      for (const link of this.links) children.get(this.nodeId(link.source)).push(this.nodeId(link.target));
      const indices = new Map();
      const low = new Map();
      const stack = [];
      const stacked = new Set();
      const components = [];
      let index = 0;
      const visit = id => {
        indices.set(id, index);
        low.set(id, index);
        index += 1;
        stack.push(id);
        stacked.add(id);
        for (const child of children.get(id)) {
          if (!indices.has(child)) {
            visit(child);
            low.set(id, Math.min(low.get(id), low.get(child)));
          } else if (stacked.has(child)) low.set(id, Math.min(low.get(id), indices.get(child)));
        }
        if (low.get(id) === indices.get(id)) {
          const component = [];
          let member;
          do {
            member = stack.pop();
            stacked.delete(member);
            component.push(member);
          } while (member !== id);
          components.push(component);
        }
      };
      for (const node of this.nodes) if (!indices.has(node.id)) visit(node.id);
      const componentOf = new Map();
      components.forEach((members, component) => members.forEach(id => componentOf.set(id, component)));
      const outgoing = components.map(() => new Set());
      const incoming = components.map(() => 0);
      const ranks = components.map(() => 0);
      for (const link of this.links) {
        const source = componentOf.get(this.nodeId(link.source));
        const target = componentOf.get(this.nodeId(link.target));
        if (source !== target && !outgoing[source].has(target)) {
          outgoing[source].add(target);
          incoming[target] += 1;
        }
      }
      const queue = incoming.flatMap((count, component) => count === 0 ? [component] : []);
      for (let cursor = 0; cursor < queue.length; cursor += 1) {
        const source = queue[cursor];
        for (const target of outgoing[source]) {
          ranks[target] = Math.max(ranks[target], ranks[source] + 1);
          incoming[target] -= 1;
          if (incoming[target] === 0) queue.push(target);
        }
      }
      for (const node of this.nodes) node.rank = ranks[componentOf.get(node.id)];
      this.packDepthRows();
    },
    packDepthRows() {
      if (!this.nodes.length || this.nodes.some(node => !Number.isInteger(node.rank))) return false;
      const availableWidth = Math.max(1, this.width - 32);
      const ranks = [...new Set(this.nodes.map(node => node.rank))].sort((a, b) => a - b);
      let rowTop = 24;
      let layoutRow = 0;
      for (const rank of ranks) {
        const rows = [];
        let row = [];
        let rowWidth = 0;
        for (const node of this.nodes.filter(node => node.rank === rank)) {
          const needed = node.width + (row.length ? 24 : 0);
          if (row.length && rowWidth + needed > availableWidth) {
            rows.push(row);
            row = [];
            rowWidth = 0;
          }
          rowWidth += node.width + (row.length ? 24 : 0);
          row.push(node);
        }
        if (row.length) rows.push(row);
        for (const cards of rows) {
          const height = Math.max(...cards.map(node => node.naturalHeight ?? node.height));
          const width = cards.reduce((total, node) => total + node.width, 0) + (cards.length - 1) * 24;
          let left = (this.width - width) / 2;
          for (const node of cards) {
            node.height = height;
            node.layoutRow = layoutRow;
            node.rowY = rowTop + height / 2;
            node.anchorX = left + node.width / 2;
            node.x = node.anchorX;
            node.y = node.rowY;
            node.fx = null;
            node.fy = node.rowY;
            node.vx = 0;
            node.vy = 0;
            left += node.width + 24;
          }
          rowTop += height + 40;
          layoutRow += 1;
        }
      }
      this.height = Math.max(400, rowTop + 24);
      return true;
    },
    rowCollision() {
      let nodes = [];
      const force = () => {
        for (let i = 0; i < nodes.length; i += 1) {
          for (let j = i + 1; j < nodes.length; j += 1) {
            const a = nodes[i];
            const b = nodes[j];
            if (a.layoutRow !== b.layoutRow) continue;
            const dx = (b.x + b.vx) - (a.x + a.vx) || 0.1;
            const overlap = (a.width + b.width) / 2 + 24 - Math.abs(dx);
            if (overlap <= 0) continue;
            const shift = Math.sign(dx) * overlap / 2;
            a.vx -= shift;
            b.vx += shift;
          }
        }
      };
      force.initialize = values => { nodes = values; };
      return force;
    },
    layoutGraph() {
      if (!this.nodes.length) return;
      this.assignRows();
      this._simulation = forceSimulation(this.nodes)
        .force('link', forceLink(this.links).id(node => node.id).distance(170).strength(0.18))
        .force('charge', forceManyBody().strength(-600))
        .force('collision', this.rowCollision())
        .force('x', forceX(node => node.anchorX).strength(0.2))
        .on('tick', () => this.constrainNodes())
        .stop();
      for (let index = 0; index < 180; index += 1) this._simulation.tick();
      this.constrainNodes();
      const simulation = this._simulation;
      select(this.$refs.graphLayer).selectAll('.entity-relationship-node')
        .data(this.nodes)
        .call(drag()
          .container(this.$refs.graphLayer)
          .on('start', (event, node) => {
            if (!event.active) simulation.alphaTarget(0.15).restart();
            node.fx = node.x;
            node.fy = node.rowY;
          })
          .on('drag', (event, node) => {
            node.fx = this.constrainX(node, event.x);
            node.fy = node.rowY;
          })
          .on('end', (event, node) => {
            if (!event.active) simulation.alphaTarget(0);
            node.fx = node.x;
            node.fy = node.rowY;
          }));
    },
    resizeGraph() {
      const width = this.$refs.container?.clientWidth;
      if (!width || width === this.width) return;
      this.width = width;
      if (!this.packDepthRows()) return;
      this._simulation?.force('x', forceX(node => node.anchorX).strength(0.2)).alpha(0.2).restart();
      this.constrainNodes();
    },
    constrainX(node, x) {
      const margin = node.width / 2 + 16;
      return Math.max(margin, Math.min(this.width - margin, x));
    },
    constrainNodes() {
      for (const node of this.nodes) {
        node.x = this.constrainX(node, node.x);
        if (node.fx !== undefined && node.fx !== null) node.fx = this.constrainX(node, node.fx);
      }
    },
    nodeId(value) {
      return typeof value === 'object' ? value.id : value;
    },
    toggleNode(id) {
      this.$emit('select', this.selectedId === id ? null : id);
    },
    isRelatedConnection(connection) {
      return connection.links.some(link => this.isRelatedLink(link));
    },
    isRelatedLink(link) {
      return this.activeId !== null && (this.nodeId(link.source) === this.activeId || this.nodeId(link.target) === this.activeId);
    },
    isRelatedNode(id) {
      return id === this.activeId || this.links.some(link => this.isRelatedLink(link) && (this.nodeId(link.source) === id || this.nodeId(link.target) === id));
    },
    linkLabel(link) {
      const source = this.nodes.find(node => node.id === this.nodeId(link.source));
      const target = this.nodes.find(node => node.id === this.nodeId(link.target));
      return `${source?.name || ''} → ${target?.name || ''}: ${link.count.toLocaleString()} direct links`;
    },
    linkPoints(link) {
      const source = link.source;
      const target = link.target;
      if (typeof source !== 'object' || typeof target !== 'object') return [];
      if (source.id === target.id) {
        const top = source.y - source.height / 2;
        return [[source.x - 20, top], [source.x - 20, top - 12], [source.x + 20, top - 12], [source.x + 20, top - 3]];
      }
      if (target.rank > source.rank) {
        const startY = source.y + source.height / 2 + 3;
        const endY = target.y - target.height / 2 - 5;
        const middleY = (startY + endY) / 2;
        return [[source.x, startY], [source.x, middleY], [target.x, middleY], [target.x, endY]];
      }
      const startY = source.y - source.height / 2 - 3;
      const endY = target.y - target.height / 2 - 5;
      const laneY = Math.min(startY, endY) - 8;
      return [[source.x, startY], [source.x, laneY], [target.x, laneY], [target.x, endY]];
    },
  },
};
</script>
